import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { CircleNotch, Ghost } from "@phosphor-icons/react";

export function PageHeader({ title, description, children, testid }) {
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between mb-8" data-testid={testid}>
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-foreground">{title}</h1>
        {description && <p className="text-sm text-muted-foreground mt-1">{description}</p>}
      </div>
      {children && <div className="flex flex-wrap items-center gap-2">{children}</div>}
    </div>
  );
}

export function StatCard({ icon: Icon, label, value, hint, testid }) {
  return (
    <div
      className="border border-border bg-card rounded-sm p-5 hover:border-primary/50 hover:-translate-y-0.5 transition-[border-color,transform] duration-200"
      data-testid={testid}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-widest text-muted-foreground">{label}</span>
        {Icon && (
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary/15">
            <Icon className="h-4 w-4 text-primary" />
          </span>
        )}
      </div>
      <div className="mt-3 font-heading text-3xl font-semibold text-foreground">{value}</div>
      {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}

export function Loading({ label = "Cargando..." }) {
  return (
    <div className="flex items-center gap-3 py-16 justify-center text-muted-foreground" data-testid="loading-indicator">
      <CircleNotch className="h-5 w-5 animate-spin" />
      <span className="text-sm">{label}</span>
    </div>
  );
}

export function EmptyState({ title, description }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="empty-state">
      <Ghost className="h-10 w-10 text-muted-foreground mb-3" />
      <p className="font-heading text-lg text-foreground/80">{title}</p>
      {description && <p className="text-sm text-muted-foreground mt-1 max-w-sm">{description}</p>}
    </div>
  );
}

const STATUS_STYLES = {
  pending: "bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/25",
  approved: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/25",
  rejected: "bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/25",
  present: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/25",
  absent: "bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/25",
  late: "bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/25",
  day_off: "bg-secondary text-muted-foreground border-border",
  vacation: "bg-sky-500/15 text-sky-600 dark:text-sky-400 border-sky-500/25",
  active: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/25",
  inactive: "bg-secondary text-muted-foreground border-border",
};

const STATUS_LABELS = {
  pending: "Pendiente",
  approved: "Aprobada",
  rejected: "Rechazada",
  present: "Presente",
  absent: "Ausente",
  late: "Tarde",
  day_off: "Descanso",
  vacation: "Vacaciones",
  active: "Activo",
  inactive: "Inactivo",
};

export function StatusBadge({ status }) {
  return (
    <Badge variant="outline" className={`rounded-sm font-normal ${STATUS_STYLES[status] || "bg-secondary text-muted-foreground border-border"}`} data-testid={`status-${status}`}>
      {STATUS_LABELS[status] || status}
    </Badge>
  );
}

export function ConfirmDialog({ open, onOpenChange, title, description, onConfirm, confirmLabel = "Eliminar", testid }) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="bg-card border-border rounded-sm">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-heading text-foreground">{title}</AlertDialogTitle>
          <AlertDialogDescription className="text-muted-foreground">{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-sm border-input bg-transparent text-foreground/80 hover:bg-accent hover:text-accent-foreground" data-testid={`${testid}-cancel`}>
            Cancelar
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            className="rounded-sm bg-red-600 text-white hover:bg-red-500"
            data-testid={`${testid}-confirm`}
          >
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
